import {
  ApiHeader,
  CustomerAccountsUsageHelper,
  KioskOrderingModeHelper,
  LocationRecommendationsLevelHelper,
  MenuModifierPriceDisplayStyleHelper,
  MenuPriceStyleHelper,
  RecommendationsFirstLoadVisibilityHelper,
  SimpleLoyaltySignupModeHelper,
} from '@biteinc/enums';

app.BiteSettingsFieldInheritanceView = app.ModallableView.extend({
  className() {
    return 'settings-field-inheritance-view';
  },

  initialize(options) {
    this.model = options.model;

    this.schema = options.schema;

    this.field = options.field;

    // Whether the current-scope settings form is editable; gates the reset-to-inherited button
    this.canEdit = !options.isReadOnly;

    const propertySchema = this.schema.fields[this.field];
    const displayName = propertySchema.displayName;
    options.title = `${displayName} (${this.field}) inheritance state`;

    options.views = [];

    app.ModallableView.prototype.initialize.apply(this, arguments);
  },

  viewWasRemovedFromDom() {
    // This view is partly used as a details view, so to ensure that it gets cleaned up properly we
    // need to emit this event
    this.trigger(app.BaseDetailsView.Events.BaseDetailsViewDidClose, this);
  },

  _getValueFromEnum(propertyName, value) {
    switch (propertyName) {
      case 'kioskOrderingMode':
        return KioskOrderingModeHelper.name(value);
      case 'customerAccountsUsage':
        return CustomerAccountsUsageHelper.name(value);
      case 'loyaltySignup.simpleMode':
        return SimpleLoyaltySignupModeHelper.name(value);
      case 'priceStyle':
        return MenuPriceStyleHelper.name(value);
      case 'modifierPriceDisplayStyle':
        return MenuModifierPriceDisplayStyleHelper.name(value);
      case 'recommendationsLevel':
        return LocationRecommendationsLevelHelper.name(value);
      case 'recommendationsFirstLoadVisibility':
      case 'recommendationsInMenuVisibility':
        return RecommendationsFirstLoadVisibilityHelper.name(value);
      default:
        return value;
    }
  },

  _getFileNameFromUrl(url) {
    const urlParts = url.split('/');
    const fileName = urlParts[urlParts.length - 1];
    return fileName;
  },

  _getImageThumbnailsHtml(images) {
    return images
      .map((image) => {
        const fileName = this._getFileNameFromUrl(image.url);
        return `<span style="white-space: nowrap"><img src="${image.url}" alt="${fileName}" title="${fileName}" style="height: 32px; width: auto; vertical-align: middle; border: 1px solid #B7B7B7; border-radius: 3px; margin: 0 6px" />${fileName}</span>`;
      })
      .join(' ');
  },

  _getValueFromType(propertyName, value) {
    const propertySchema = this.schema.fields[propertyName];
    if ([undefined, null].includes(value) && propertySchema.type !== 'bool') {
      return 'Unset';
    }
    switch (propertySchema.type) {
      case 'bool':
        return value ? 'Enabled' : 'Disabled';
      case 'int':
        return this._getValueFromEnum(propertyName, value);
      case 'array': {
        if (!value) {
          return 'Unset';
        }
        if (!value.length) {
          return 'Empty';
        }
        if (propertySchema.elementSubtype === 'Image') {
          return this._getImageThumbnailsHtml(value);
        }
        return value.join(', ');
      }
      case 'shortString':
      case 'keyString':
      case 'stringEnum': {
        if (value === '') {
          return 'Empty String';
        }
        return this._getValueFromEnum(propertyName, value);
      }
      case 'price':
        // Values are stored in cents
        return (value / 100).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      case 'object': {
        if (!value) {
          return 'Unset';
        }
        if (!Object.keys(value).length) {
          return 'Empty';
        }
        return `<pre>${JSON.stringify(value, null, 2)}</pre>`;
      }
      default:
        return value;
    }
  },

  _linkedName(displayName, path) {
    if (!path) {
      return displayName;
    }
    const tabId = this.schema.fields[this.field].tabId;
    return `<a href="${path}#${tabId}" style="color: inherit; text-decoration: underline">${displayName}</a>`;
  },

  _getAllSiteLocationSettings(fieldName) {
    return app.allSiteSettings.map((siteSetting) => {
      const site = app.siteById[siteSetting.get('siteId')];
      const siteName = site
        ? this._linkedName(site.get('name'), site.bureauUrl())
        : siteSetting.get('siteId');
      const valueStr = this._getValueFromType(fieldName, siteSetting.get(fieldName));
      const isOverridden = siteSetting.get('overrideSet').includes(fieldName);
      const divStyleParts = ['border-bottom: 1px solid #B7B7B7'];
      if (isOverridden) {
        divStyleParts.push('color: red');
      }
      const spanStyleParts = [];
      if (`${valueStr}`.length < 20) {
        spanStyleParts.push('float: right');
      }
      return `<div style="${divStyleParts.join(';')}">${siteName} <span style="${spanStyleParts.join(';')}">${valueStr}</span></div>`;
    });
  },

  _getAllLocationSettings(fieldName) {
    return app.allLocationSettings.map((locationSetting) => {
      const location = app.locationById[locationSetting.get('locationId')];
      const locationInfo = location
        ? this._linkedName(
            `${location.get('name')} <i>(${location.get('channelName')})</i>`,
            location.bureauUrl(),
          )
        : locationSetting.get('locationId');
      const isOverridden = locationSetting.get('overrideSet').includes(fieldName);
      const valueStr = this._getValueFromType(fieldName, locationSetting.get(fieldName));
      const divStyleParts = ['border-bottom: 1px solid #B7B7B7'];
      if (isOverridden) {
        divStyleParts.push('color: red');
      }
      const spanStyleParts = [];
      if (`${valueStr}`.length < 20) {
        spanStyleParts.push('float: right');
      }
      return `<div style="${divStyleParts.join(';')}">${locationInfo} <span style="${spanStyleParts.join(';')}">${valueStr}</span></div>`;
    });
  },

  _generateOrgChildrenHtml(fieldName) {
    const allSiteSettings = this._getAllSiteLocationSettings(fieldName);
    const allLocationSettings = this._getAllLocationSettings(fieldName);

    const parts = [];
    if (!allSiteSettings.length) {
      parts.push(`<div>This org has no sites</div>`);
    } else {
      parts.push(...['<br><div><b>Sites</b><div>', ...allSiteSettings]);
    }

    if (!allLocationSettings.length) {
      parts.push(`<div>This org has no channels</div>`);
    } else {
      parts.push(...['<br><div><b>Channels</b><div>', ...allLocationSettings]);
    }
    return parts.join('');
  },

  _generateSiteChildrenHtml(fieldName) {
    const allLocationSettings = this._getAllLocationSettings(fieldName);
    const parts = [];
    if (!allLocationSettings.length) {
      parts.push(`<div>This site has no channels</div>`);
    } else {
      parts.push(...['<br><div><b>Channels</b><div>', ...allLocationSettings]);
    }
    return parts.join('');
  },

  _canForceSetting() {
    switch (this.model.get('scope')) {
      case 'org':
        return app.sessionUser.isOrgWide();
      case 'site':
        return app.sessionUser.isSiteWide();
      default:
        return false;
    }
  },

  _applyForcedFieldToDescendants(field, value) {
    // The server flattened the value onto every descendant and cleared the field from their
    // overrideSets. Mirror that deterministic result on the in-memory models.
    const descendantSettingsModels =
      this.model.get('scope') === 'org'
        ? [...app.allSiteSettings, ...app.allLocationSettings]
        : app.allLocationSettings.filter((channelSettings) => {
            return channelSettings.get('siteId') === this.model.get('siteId');
          });
    descendantSettingsModels.forEach((settingsDoc) => {
      settingsDoc.set(field, value);
      settingsDoc.set(
        'overrideSet',
        (settingsDoc.get('overrideSet') || []).filter((overrideField) => {
          return overrideField !== field;
        }),
      );
    });
  },

  _forceSettingButton() {
    const $button = $(
      `<button type="button" class="btn btn-secondary btn-sm" style="float: right">Force This Setting</button>`,
    );
    $button.click(() => {
      const reqId = $button.initLoadingButton('Force This Setting', 'Forcing', 'Forced');
      // One server-authoritative call flattens the whole subtree in a single transaction, so we
      // no longer iterate children or depend on request ordering.
      app.makeRequestWithOptions({
        method: 'PUT',
        url: `${this.model.url()}/force`,
        data: { field: this.field },
        onSuccess: (data) => {
          this._applyForcedFieldToDescendants(data.field, data.value);
          $button.loadingDidFinishSuccessfully(reqId);
          this._renderChildRows();
          this.trigger(app.BiteSettingsFieldInheritanceView.Events.SettingsWereForced);
        },
        onFailure: () => {
          $button.loadingDidFinishWithError(reqId);
        },
      });
      return false;
    });
    return $button;
  },

  _canResetToInherited() {
    // Only levels with a parent can inherit; org has none. Gate by the form's editability so a
    // location/site manager can self-serve on the scope they can already edit.
    return this.canEdit && ['site', 'channel'].includes(this.model.get('scope'));
  },

  // Buttons that copy an ancestor's value into this doc. A channel can pull from its site or,
  // skipping the site's override, straight from the org; a site can pull from the org.
  _resetSourceScopeNames() {
    return this.model.get('scope') === 'channel' ? ['site', 'org'] : ['org'];
  },

  _valueForSourceScopeName(sourceScopeName) {
    return sourceScopeName === 'org'
      ? app.orgSettings.get(this.field)
      : app.siteSettings.get(this.field);
  },

  _resetToValueButton(sourceScopeName) {
    const buttonText = `Use the value from the ${sourceScopeName}`;
    const $button = $(
      `<button type="button" class="btn btn-secondary btn-sm" style="float: right; margin-right: 6px">${buttonText}</button>`,
    );
    $button.click(() => {
      const reqId = $button.initLoadingButton(buttonText, 'Resetting', 'Reset');
      const parentValue = this._valueForSourceScopeName(sourceScopeName);
      const headers = { [ApiHeader.SiteId]: this.model.get('siteId') };
      if (this.model.get('scope') === 'channel') {
        headers[ApiHeader.LocationId] = this.model.get('locationId');
      }

      app.makeRequestWithOptions({
        method: 'PUT',
        url: this.model.url(),
        // The server recomputes overrideSet against the IMMEDIATE parent: if this value equals it,
        // the key drops and inheritance resumes; otherwise it stays an override with the chosen
        // ancestor's content (e.g. org value at a channel whose site overrides). Reads never merge,
        // so we copy the value down, not just clear the flag.
        data: { [this.field]: parentValue },
        headers,
        onSuccess: (data) => {
          this.model.set(data.settings);
          if (this.model.get('scope') === 'site') {
            // The server cascades the new value to the site's non-overriding channel docs, but
            // only returns the site doc; mirror that on the in-memory models.
            (app.allLocationSettings || [])
              .filter((channelSettings) => {
                return (
                  channelSettings.get('siteId') === this.model.get('siteId') &&
                  !channelSettings.overridesField(this.field)
                );
              })
              .forEach((channelSettings) => {
                channelSettings.set(this.field, parentValue);
              });
          }
          $button.loadingDidFinishSuccessfully(reqId);
          this._renderChildRows();
          this.trigger(app.BiteSettingsFieldInheritanceView.Events.SettingsWereReset);
        },
        onFailure: () => {
          $button.loadingDidFinishWithError(reqId);
        },
      });
      return false;
    });
    return $button;
  },

  _renderChildRows() {
    const fieldName = this.field;
    switch (this.model.get('scope')) {
      case 'org':
        this.$childRowsContainer.html(this._generateOrgChildrenHtml(fieldName));
        break;
      case 'site':
        this.$childRowsContainer.html(this._generateSiteChildrenHtml(fieldName));
        break;
      default:
        this.$childRowsContainer.empty();
    }
  },

  render() {
    app.ModallableView.prototype.render.apply(this, arguments);

    const fieldName = this._options.field;
    const scope = this.model.get('scope');

    if (scope !== 'channel') {
      this.$el.append(
        `<div><i><span style="color: red">Red</span> means the site/channel overrides its parent's setting</i></div>`,
      );
    }

    const currentLevelValue = this._getValueFromType(fieldName, this.model.get(fieldName));
    const lines = [];
    if (scope === 'org') {
      lines.push($(`<div>Org: ${currentLevelValue}</div>`));
    } else {
      const orgValue = this._getValueFromType(fieldName, app.orgSettings.get(fieldName));
      lines.push($(`<div>Org: ${orgValue}</div>`));
      if (scope === 'site') {
        lines.push($(`<div>Site: ${currentLevelValue}</div>`));
      } else {
        const siteValue = this._getValueFromType(fieldName, app.siteSettings.get(fieldName));
        lines.push($(`<div>Site: ${siteValue}</div>`));
        lines.push($(`<div>Channel: ${currentLevelValue}</div>`));
      }
    }
    // The last line is always the current scope's value. Force pushes this scope's value down to
    // children; reset pulls an ancestor's value into this scope. Both can apply at a site.
    if (this._canForceSetting()) {
      lines[lines.length - 1].append(this._forceSettingButton());
    }
    if (this._canResetToInherited()) {
      // Buttons float right, so the last appended sits leftmost; append furthest ancestor first so
      // the nearest ancestor reads left-to-right (e.g. site then org at the channel level).
      this._resetSourceScopeNames()
        .slice()
        .reverse()
        .forEach((sourceScopeName) => {
          lines[lines.length - 1].append(this._resetToValueButton(sourceScopeName));
        });
    }
    lines.forEach(($line) => this.$el.append($line));

    this.$childRowsContainer = $('<div></div>');
    this.$el.append(this.$childRowsContainer);
    this._renderChildRows();

    return this;
  },
});

app.BiteSettingsFieldInheritanceView.Events = {
  SettingsWereForced: 'SettingsWereForced',
  SettingsWereReset: 'SettingsWereReset',
};
